<template>
  <div title="Schedule">
    <RfAlert v-if="!model.is_enabled && itemType !== 'experiment'" type="warning">
      Note: This {{ itemType }} is currently a draft. To activate, start the
      {{ itemType }} manually.
    </RfAlert>
    <div class="text-body my-4 flex flex-col gap-2">
      <span> Use the schedule settings to control when this {{ itemType }} is active. </span>
      <span>
        The default schedule is based on your app's time zone:
        <span class="font-bold">{{ appsStore.app?.timezone_offset }}</span
        >. This means the {{ itemType }} will automatically be active starting at midnight (12:00
        AM) on the start date and ending at 11:59 PM on the end date.
      </span>
    </div>
    <div class="add-schedule-date-time-div">
      <RfStartStopDatePicker
        v-if="scheduleType === 'schedule'"
        ref="dateRangePicker"
        :dateRange="dateRange"
        :disableStartDate="disableStartDate"
        :hideTime="hideTime"
        :model="model"
        :showClearIcon="showClearIcon"
        :experiments="experiments"
      />
      <RfSchedulePicker
        v-if="scheduleType === 'daypart'"
        ref="schedulePicker"
        :scheduleRanges="scheduleRanges"
        :itemType="itemType"
      />
      <div v-if="scheduleType === 'daypart'" class="rf-section-title" style="padding-top: 20px">
        <div class="schedule-blurbs">
          <h3>Specify a timezone for this schedule. Default is user timezone.</h3>
        </div>
      </div>
      <div v-if="scheduleType === 'daypart'">
        <v-radio-group v-model="is_app_timezone" inline hideDetails>
          <v-radio :value="false" label="User Timezone"></v-radio>
          <v-radio :value="true" label="App Timezone"></v-radio>
        </v-radio-group>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import RfSchedulePicker from "../RfSchedulePicker.vue";
import RfStartStopDatePicker from "../RfStartStopDatePicker.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import { useAppsStore } from "@/pinia/appsStore";

export default {
  name: "RfSchedule",
  components: {
    RfSchedulePicker,
    RfStartStopDatePicker,
    RfAlert,
  },
  props: {
    model: Object,
    scheduleType: String,
    isPushNotification: { type: Boolean, default: false },
    hideTime: { type: Boolean, default: false },
    experiments: { type: Boolean, default: false },
    showClearIcon: { type: Boolean, default: false },
    disableStartDate: { type: Boolean, default: false },
  },
  setup: () => ({
    appsStore: useAppsStore(),
  }),
  data() {
    return {
      is_app_timezone: this.model?.is_app_timezone,
    };
  },
  computed: {
    itemType() {
      if (this.model.subject_name === "Experiment") {
        return "experiment";
      }
      if (this.model.subject_name === "Sequence") {
        return "guide";
      }
      return "prompt";
    },
    dateRange() {
      const range = {
        startDate: dayjs.utc(this.model.start_date),
        endDate: dayjs.utc(this.model.end_date),
      };
      return range;
    },
    scheduleRanges() {
      return this.model.filter.schedule;
    },
  },
  watch: {
    model(to) {
      this.is_app_timezone = to.is_app_timezone;
    },
  },
  methods: {
    fillModel(model) {
      if (this.$refs.dateRangePicker) {
        const {
          fullStartDate,
          fullEndDate,
          isEventTriggered,
          frequency,
          localTime,
          quietTimeStart,
          quietTimeEnd,
        } = this.$refs.dateRangePicker;
        if (dayjs(fullStartDate) > dayjs(fullEndDate)) {
          throw new Error("Start date must be before end date");
        }
        model.start_date = fullStartDate;
        model.end_date = fullEndDate;
        if (this.isPushNotification) {
          model.actions.rf_pinpoint_campaign_event_triggered = isEventTriggered ? "true" : "false";
          model.actions.rf_push_frequency = frequency;
          model.actions.rf_push_use_local_time = localTime;
          model.actions.rf_pinpoint_campaign_quiet_time_start = quietTimeStart;
          model.actions.rf_pinpoint_campaign_quiet_time_end = quietTimeEnd;
        }
      }
      if (this.$refs.schedulePicker) {
        model.filter.schedule = this.$refs.schedulePicker.schedule;
        model.is_app_timezone = this.is_app_timezone;
      }
    },
  },
};
</script>
